/**
 * Hook del detalle de un Cliente (US-2.3). La red vive en `customerApi`; acá solo el
 * estado de la vista (loading / no encontrado / error / refresh), separable de la
 * presentación — mismo patrón que `useAlly`.
 */
import { useCallback, useEffect, useState } from 'react';
import type { Customer } from '@goelectric/shared-types';
import { ApiError } from '@/lib/http/apiClient';
import { customerApi } from '../api/customerApi';

export interface UseCustomerResult {
  customer: Customer | null;
  loading: boolean;
  error: string | null;
  /** `true` cuando el backend responde 404 (cliente inexistente). */
  notFound: boolean;
  refresh: () => void;
}

function toErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return 'No se pudo cargar el cliente.';
}

export function useCustomer(customerId: string | undefined): UseCustomerResult {
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!customerId) {
      setLoading(false);
      setNotFound(true);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setNotFound(false);

    customerApi
      .get(customerId)
      .then((result) => {
        if (!cancelled) setCustomer(result);
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        if (caught instanceof ApiError && caught.status === 404) {
          setNotFound(true);
        } else {
          setError(toErrorMessage(caught));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [customerId, reloadKey]);

  const refresh = useCallback(() => setReloadKey((key) => key + 1), []);

  return { customer, loading, error, notFound, refresh };
}
